import { useMemo, useState } from 'react';

import { useTranslation } from 'react-i18next';
import { Link as RouterLink } from 'react-router-dom';

import GppMaybeRoundedIcon from '@mui/icons-material/GppMaybeRounded';
import LockRoundedIcon from '@mui/icons-material/LockRounded';
import TuneRoundedIcon from '@mui/icons-material/TuneRounded';
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Skeleton,
  Snackbar,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';

import { capabilityLabelKey, domainLabelKey, levelLabelKey } from '@/shared/access';
import type { AccessGroup } from '@/shared/access';
import { EmptyState, ErrorState, LoadingState } from '@/shared/ui';

import { useSetSubjectGrants } from '../../hooks';
import type { DepartmentRights } from '../../lib/departmentRights';
import { descendantsOf } from '../../lib/groupHierarchy';
import { LEVEL_COLOR } from '../../lib/levelColor';
import { useDepartmentReview } from '../../useDepartmentReview';

export interface DepartmentAttestationProps {
  onOpenHistory?: () => void;
  /** Narrows the list by department name or path — driven by the review toolbar. */
  search?: string;
  /** Only departments carrying a flag: high-risk rights, ADMIN, or rights with nobody in them. */
  flaggedOnly?: boolean;
}

/**
 * The attestation half of the access review (plan-auth-001 Phase 4.5).
 *
 * <p>The employee listing beside this one asks "should this *person* still have this?". This one asks the
 * question the rights are actually attached to: <em>should this **department** still confer this?</em>
 * Rights live on departments and people inherit them, so a department nobody has looked at since it was
 * created is exactly where drift accumulates — and it is invisible in a per-person matrix.
 *
 * <p><b>Revoking is recorded; confirming is not.</b> A revoke goes through the same grant write the matrix
 * editor uses, so it lands in the append-only access history the reviewer can open right here. There is no
 * platform endpoint that records "I looked at this department and it is correct" — so this screen does not
 * offer a Confirm button that would persist nothing and give a reviewer false assurance. The honest
 * evidence for an unchanged department is today's export, and the inline note says so rather than leaving
 * the reviewer to discover it.
 */
export function DepartmentAttestation({
  onOpenHistory,
  search = '',
  flaggedOnly = false,
}: DepartmentAttestationProps) {
  const { t } = useTranslation('access');

  const review = useDepartmentReview();
  const revoke = useSetSubjectGrants();

  const [confirming, setConfirming] = useState<AccessGroup | null>(null);
  const [revoked, setRevoked] = useState<string | null>(null);

  const groups = useMemo(() => review.rows.map((row) => row.group), [review.rows]);

  const query = search.trim().toLowerCase();
  const shown = useMemo(
    () =>
      review.rows.filter((row) => {
        if (flaggedOnly && !row.needsAttention) return false;
        if (!query) return true;
        return (
          row.group.name.toLowerCase().includes(query) ||
          (row.group.path ?? '').toLowerCase().includes(query)
        );
      }),
    [review.rows, flaggedOnly, query],
  );

  const subDepartments = useMemo(
    () => (confirming ? descendantsOf(groups, confirming.id) : []),
    [groups, confirming],
  );

  if (review.isLoading) {
    return <LoadingState label={t('review.departments.loading')} />;
  }
  if (review.isError) {
    return (
      <ErrorState
        title={t('review.departments.loadError')}
        message={review.errorMessage}
        onRetry={review.refetch}
      />
    );
  }

  const confirmingRights =
    review.rows.find((row) => row.group.id === confirming?.id)?.rights ?? null;

  return (
    <Stack spacing={1.5} sx={{ minWidth: 0 }}>
      <Box>
        <Typography variant="h6">{t('review.departments.title')}</Typography>
        <Typography variant="body2" color="text.secondary">
          {t('review.departments.subtitle')}
        </Typography>
      </Box>

      <Alert
        severity="info"
        action={
          onOpenHistory ? (
            <Button size="small" color="inherit" onClick={onOpenHistory}>
              {t('review.history')}
            </Button>
          ) : undefined
        }
      >
        {t('review.departments.evidenceNote')}
      </Alert>

      {review.rows.length === 0 ? (
        <EmptyState
          title={t('review.departments.empty.title')}
          description={t('review.departments.empty.description')}
        />
      ) : shown.length === 0 ? (
        <EmptyState
          title={
            flaggedOnly
              ? t('review.departments.noFlagged.title')
              : t('review.departments.noMatches.title')
          }
          description={
            flaggedOnly
              ? t('review.departments.noFlagged.description')
              : t('review.departments.noMatches.description')
          }
        />
      ) : (
        <TableContainer sx={{ overflowX: 'auto' }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>{t('review.departments.table.department')}</TableCell>
                <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                  {t('review.departments.table.members')}
                </TableCell>
                <TableCell>{t('review.departments.table.rights')}</TableCell>
                <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                  {t('review.departments.table.decision')}
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {shown.map(({ group, rights, isLoading, isError }) => (
                <TableRow key={group.id} hover>
                  <TableCell>
                    <Stack direction="row" spacing={0.5} alignItems="center">
                      <Box>
                        <Typography variant="body2" fontWeight={600}>
                          {group.name}
                        </Typography>
                        {group.path && group.path !== `/${group.name}` && (
                          <Typography variant="caption" color="text.secondary">
                            {group.path}
                          </Typography>
                        )}
                      </Box>
                      {group.builtIn && (
                        <Tooltip title={t('review.departments.builtIn')}>
                          <LockRoundedIcon fontSize="inherit" color="disabled" />
                        </Tooltip>
                      )}
                    </Stack>
                  </TableCell>
                  <TableCell align="right">
                    <Typography
                      variant="body2"
                      color={group.memberCount === 0 ? 'text.disabled' : 'text.primary'}
                    >
                      {group.memberCount}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    {isLoading ? (
                      <Skeleton variant="text" width={180} />
                    ) : isError ? (
                      <Typography variant="caption" color="error">
                        {t('review.departments.rightsError')}
                      </Typography>
                    ) : (
                      <RightsCell
                        group={group}
                        rights={rights}
                        memberless={group.memberCount === 0}
                      />
                    )}
                  </TableCell>
                  <TableCell align="right">
                    <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                      <Button
                        size="small"
                        startIcon={<TuneRoundedIcon />}
                        component={RouterLink}
                        to={`/team/access?subjectType=GROUP&subjectId=${encodeURIComponent(group.id)}`}
                      >
                        {t('review.departments.adjust')}
                      </Button>
                      <Button
                        size="small"
                        color="error"
                        disabled={rights.empty || isLoading || isError}
                        onClick={() => {
                          revoke.reset();
                          setConfirming(group);
                        }}
                      >
                        {t('review.departments.revoke')}
                      </Button>
                    </Stack>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <Dialog open={confirming !== null} onClose={() => setConfirming(null)}>
        <DialogTitle>
          {t('review.departments.confirmRevoke.title', { name: confirming?.name ?? '' })}
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            {t('review.departments.confirmRevoke.body', {
              name: confirming?.name ?? '',
              count: confirming?.memberCount ?? 0,
            })}
          </DialogContentText>
          {confirmingRights && confirmingRights.domains.length > 0 && (
            <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap sx={{ mt: 1.5 }}>
              {confirmingRights.domains.map((entry) => (
                <Chip
                  key={entry.domainCode}
                  size="small"
                  color={LEVEL_COLOR[entry.level]}
                  label={`${t(domainLabelKey(entry.domainCode), entry.domainCode)} · ${t(levelLabelKey(entry.level))}`}
                />
              ))}
            </Stack>
          )}
          {subDepartments.length > 0 && (
            <Alert severity="warning" sx={{ mt: 2 }}>
              {t('review.departments.confirmRevoke.inheritedWarning', {
                count: subDepartments.length,
              })}
            </Alert>
          )}
          {confirming && review.myDepartments.has(confirming.id) && (
            <Alert severity="warning" sx={{ mt: 2 }}>
              {t('review.departments.confirmRevoke.selfWarning')}
            </Alert>
          )}
          {revoke.isError && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {revoke.error?.message ?? t('review.departments.confirmRevoke.error')}
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirming(null)} disabled={revoke.isPending}>
            {t('common:action.cancel')}
          </Button>
          <Button
            color="error"
            variant="contained"
            disabled={revoke.isPending}
            onClick={() => {
              if (!confirming) return;
              const name = confirming.name;
              revoke.mutate(
                {
                  subjectType: 'GROUP',
                  subjectId: confirming.id,
                  // A replace, not a merge: an empty map takes every domain to NONE, and an empty
                  // exception list clears the capability allows/denies with it.
                  body: { levels: {}, capabilities: [] },
                },
                {
                  onSuccess: () => {
                    setConfirming(null);
                    setRevoked(name);
                  },
                },
              );
            }}
          >
            {t('review.departments.confirmRevoke.action')}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={revoked !== null}
        autoHideDuration={8000}
        onClose={() => setRevoked(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity="success" variant="filled" onClose={() => setRevoked(null)}>
          {t('review.departments.revoked', { name: revoked ?? '' })}
        </Alert>
      </Snackbar>
    </Stack>
  );
}

/** The rights a department confers, plus the two shapes a reviewer should stop on. */
function RightsCell({
  group,
  rights,
  memberless,
}: {
  group: AccessGroup;
  rights: DepartmentRights;
  memberless: boolean;
}) {
  const { t } = useTranslation('access');

  if (rights.empty) {
    return (
      <Typography variant="caption" color="text.disabled">
        {t('review.departments.noRights')}
      </Typography>
    );
  }

  return (
    <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap alignItems="center">
      {rights.domains.map((entry) => (
        <Chip
          key={`${group.id}:${entry.domainCode}`}
          size="small"
          color={LEVEL_COLOR[entry.level]}
          label={`${t(domainLabelKey(entry.domainCode), entry.domainCode)} · ${t(levelLabelKey(entry.level))}`}
        />
      ))}
      {rights.elevated.length > 0 && (
        <Tooltip
          title={rights.elevated
            .map((entry) => t(capabilityLabelKey(entry.capabilityCode), entry.capabilityCode))
            .join(', ')}
        >
          <Chip
            size="small"
            color="warning"
            variant="outlined"
            icon={<GppMaybeRoundedIcon />}
            label={t('review.departments.flags.elevated', { count: rights.elevated.length })}
          />
        </Tooltip>
      )}
      {memberless && (
        <Tooltip title={t('review.departments.flags.memberlessHint')}>
          <Chip size="small" variant="outlined" label={t('review.departments.flags.memberless')} />
        </Tooltip>
      )}
    </Stack>
  );
}
